import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Cloud, Globe, Calculator, FileText, Building, Mail } from 'lucide-react';
import { products } from '../../data/products';
import { solutionCategories } from '../../data/solutionCategories';
import { solutions } from '../../data/solutions';
import { resourcesGroups } from './navData';

const linkBase = 'flex items-center justify-between px-5 py-3.5 rounded-lg transition-colors';
const linkIdle = 'text-[#CBD5E1] hover:text-white hover:bg-white/[0.06]';
const linkActive = 'bg-[#F29B3D]/12 text-white border border-[#F29B3D]/25';

const Accordion = ({ id, icon: Icon, label, open, onToggle, children }) => (
  <div className="border-b border-white/[0.06]">
    <button
      data-testid={`mobile-accordion-${id}`}
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 text-[#CBD5E1] hover:text-white transition-colors"
    >
      <span className="flex items-center gap-3.5 font-semibold text-base">
        <Icon className="w-5 h-5 text-[#F29B3D]/80" strokeWidth={1.5} />
        {label}
      </span>
      <ChevronDown className={`w-4.5 h-4.5 transition-transform duration-200 ${open ? 'rotate-180 text-[#F29B3D]' : ''}`} />
    </button>
    <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-[700px] pb-3' : 'max-h-0'}`}>
      {children}
    </div>
  </div>
);

const SubLink = ({ to, testId, children }) => (
  <Link to={to} data-testid={testId} className="block px-12 py-2.5 text-[15px] text-[#93A5BC] hover:text-white transition-colors">
    {children}
  </Link>
);

const SubHeading = ({ children }) => (
  <p className="px-12 pt-3 pb-1 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#94A3B8]">{children}</p>
);

export const MobileMenu = ({ isMobileMenuOpen, setIsMobileMenuOpen, isActive }) => {
  const [openSection, setOpenSection] = useState(null);
  const toggle = (id) => setOpenSection(prev => (prev === id ? null : id));

  return (
    <>
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-0 bg-[#020a14]/60 z-30" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      <div className={`lg:hidden bg-[#120836] border-t border-white/10 overflow-y-auto overscroll-contain transition-all duration-300 relative z-40 ${
        isMobileMenuOpen ? 'h-[calc(100dvh-68px)] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
      }`}>
        <div className="px-4 pt-3 pb-8">
          <nav className="flex flex-col">
            <Accordion id="products" icon={Cloud} label="Products" open={openSection === 'products'} onToggle={() => toggle('products')}>
              <SubLink to="/products" testId="mobile-nav-products">All Products</SubLink>
              {products.map(p => (
                <SubLink key={p.id} to={`/products/${p.id}`} testId={`mobile-nav-product-${p.id}`}>{p.name}</SubLink>
              ))}
              <SubLink to="/services" testId="mobile-nav-full-catalog">Full Service Catalog</SubLink>
            </Accordion>

            <Accordion id="solutions" icon={Globe} label="Solutions" open={openSection === 'solutions'} onToggle={() => toggle('solutions')}>
              <SubLink to="/solutions" testId="mobile-nav-solutions">All Solutions</SubLink>
              {solutionCategories.map(cat => (
                <SubLink key={cat.id} to={cat.href || `/solutions#${cat.id}`} testId={`mobile-nav-cat-${cat.id}`}>{cat.name}</SubLink>
              ))}
              <SubHeading>Industries</SubHeading>
              {solutions.map(sol => (
                <SubLink key={sol.id} to={`/solutions/${sol.id}`} testId={`mobile-nav-ind-${sol.id}`}>{sol.name}</SubLink>
              ))}
              <SubLink to="/migrate" testId="mobile-nav-migrate">Migrate from VMware</SubLink>
            </Accordion>

            <Link to="/pricing" data-testid="mobile-nav-pricing" className={`${linkBase} border-b border-white/[0.06] ${isActive('/pricing') ? linkActive : linkIdle}`}>
              <span className="flex items-center gap-3.5 font-semibold text-base">
                <Calculator className="w-5 h-5 text-[#F29B3D]/80" strokeWidth={1.5} /> Pricing
              </span>
            </Link>

            <Accordion id="resources" icon={FileText} label="Resources" open={openSection === 'resources'} onToggle={() => toggle('resources')}>
              {resourcesGroups.map(group => (
                <div key={group.heading}>
                  <SubHeading>{group.heading}</SubHeading>
                  {group.items.map(link => (
                    <SubLink key={link.name} to={link.href} testId={`mobile-nav-${link.name.toLowerCase().replace(/\s+|&/g, '-').replace(/--+/g, '-')}`}>{link.name}</SubLink>
                  ))}
                </div>
              ))}
            </Accordion>

            <Accordion id="company" icon={Building} label="Company" open={openSection === 'company'} onToggle={() => toggle('company')}>
              <SubLink to="/platform" testId="mobile-nav-platform">Platform</SubLink>
              <SubLink to="/why-daakyi" testId="mobile-nav-why-daakyi">Why DAAKYI</SubLink>
              <SubLink to="/about" testId="mobile-nav-about-us">About Us</SubLink>
              <SubLink to="/security" testId="mobile-nav-security">Security</SubLink>
              <SubLink to="/contact" testId="mobile-nav-contact">Contact</SubLink>
            </Accordion>

            <div className="mt-6 space-y-3 px-1">
              <Link to="/console" data-testid="mobile-console-signin"
                className="flex items-center justify-center gap-2 w-full border border-white/20 text-[#CBD5E1] font-medium py-3 rounded-lg text-[15px]">
                Sign In to Console
              </Link>
              <Link to="/cloud-access" data-testid="mobile-request-access"
                className="flex items-center justify-center gap-2 w-full bg-[#F29B3D] text-white font-bold py-3.5 rounded-lg text-[15px]">
                <Mail className="w-4 h-4" strokeWidth={1.5} /> Request Cloud Access
              </Link>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};
